import {CommonModule} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  cpfCnpjNoLiveMask,
  detectDocumentKind,
  documentInputMaxLength,
  formatDocumentDisplay,
} from '@gatewee/utils/helpers/cpf-cnpj-input.helper';
import {
  DetectedDocumentKind,
  DocumentInputType,
  validateDocumentByType,
} from '@gatewee/utils/validators/cpf-cnpjs-validators';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {GateweeInputV2Component} from '../gatewee-input-v2/gatewee-input-v2.component';

export type {DetectedDocumentKind} from '@gatewee/utils/validators/cpf-cnpjs-validators';

@Component({
  selector: 'gatewee-document-input',
  standalone: true,
  imports: [CommonModule, GateweeInputV2Component],
  templateUrl: './gatewee-document-input.component.html',
  styleUrls: ['./gatewee-document-input.component.css'],
})
export class GateweeDocumentInputComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() documentType: DocumentInputType = 'auto';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({required: true}) form!: FormGroup | any;
  @Input({required: true}) controlName!: string;
  @Input({required: true}) id!: string;
  @Input() label!: string;
  @Input() placeholder = '';
  @Input() required = false;
  @Input() submitted = false;
  @Input() customMessages: Record<string, string> = {};
  @Input() hint!: {message: string; icon: string};
  @Input() info!: string;
  @Input() isLoading = false;
  @Input() readonly = false;
  @Input() class = '';

  /** Emits whenever the inferred CPF/CNPJ kind changes (`null` when empty). */
  @Output() detectedTypeChange =
    new EventEmitter<DetectedDocumentKind | null>();
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<FocusEvent>();

  readonly documentMask = cpfCnpjNoLiveMask;

  private valueSub?: Subscription;

  constructor(private readonly translate: TranslateService) {}

  ngOnInit(): void {
    this.syncValidators();
    this.watchDetectedType();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['documentType'] ||
      changes['required'] ||
      changes['form'] ||
      changes['controlName']
    ) {
      this.syncValidators();
      this.watchDetectedType();
    }
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
  }

  get maxLength(): number {
    return documentInputMaxLength(this.documentType);
  }

  get control(): FormControl | null {
    if (!this.form || !this.controlName) {
      return null;
    }
    return this.form.get(this.controlName) as FormControl | null;
  }

  get resolvedCustomMessages(): Record<string, string> {
    const p = 'common.documentInput';
    return {
      required: this.translate.instant(`${p}.required`),
      invalidCPF: this.translate.instant(`${p}.invalidCPF`),
      invalidCNPJ: this.translate.instant(`${p}.invalidCNPJ`),
      invalidDocument: this.translate.instant(`${p}.invalidDocument`),
      ...this.customMessages,
    };
  }

  onBlur(): void {
    const ctrl = this.control;
    if (!ctrl) {
      this.blur.emit();
      return;
    }

    const formatted = formatDocumentDisplay(ctrl.value, this.documentType);
    if (formatted !== String(ctrl.value ?? '')) {
      ctrl.setValue(formatted, {emitEvent: false});
    }

    ctrl.markAsTouched();
    ctrl.updateValueAndValidity();
    this.emitDetectedType(ctrl.value);
    this.blur.emit();
  }

  private watchDetectedType(): void {
    this.valueSub?.unsubscribe();
    const ctrl = this.control;
    if (!ctrl) {
      return;
    }

    this.emitDetectedType(ctrl.value);
    this.valueSub = ctrl.valueChanges.subscribe(value =>
      this.emitDetectedType(value),
    );
  }

  private emitDetectedType(value: unknown): void {
    this.detectedTypeChange.emit(
      detectDocumentKind(
        value === null || value === undefined ? '' : String(value),
        this.documentType,
      ),
    );
  }

  private syncValidators(): void {
    const ctrl = this.control;
    if (!ctrl) {
      return;
    }

    const validators = [validateDocumentByType(this.documentType)];
    if (this.required) {
      validators.unshift(Validators.required);
    }

    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({emitEvent: false});
  }
}
